import {useAuth} from '@common/auth/use-auth';
import {useNavigate, useLocation} from 'react-router';
import {useEffect, useRef, useState} from 'react';
import {useCompleteTour} from './use-complete-tour';
import {tourStore} from './tour-store';
import {Link} from 'react-router';

type StepType = 'modal' | 'spotlight';
type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

interface Step {
  id: string;
  type: StepType;
  headline?: string;
  title: string;
  body?: string;
  navigateTo?: string;
  targetSelector?: string;
  tooltipSide?: TooltipSide;
  primaryCta?: string;
  secondaryCta?: string;
  cta?: string;
  ctaPath?: string;
}

const STEPS: Step[] = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Welcome to Fair Music Beta',
    primaryCta: 'Start the Tour',
  },
  {
    id: 'smart-record',
    type: 'spotlight',
    headline: 'Unprecedented Metadata Transparency.',
    title: 'Beyond the Play Button',
    body: 'Traditional platforms hide who actually made the music. FairMusic acts as a "Smart Record," displaying blockchain-verified credits in real-time. Every contributor -- from the songwriter to the session musician -- is recognized and paid accurately.',
    targetSelector: '[data-tour="player"]',
    tooltipSide: 'top',
    cta: 'Next',
  },
  {
    id: 'fair-pay',
    type: 'spotlight',
    headline: 'Protecting Human Creation.',
    title: 'Fair Pay in the AI Era',
    body: 'The old pro-rata model drowns your revenue in a sea of automated tracks. We offer total transparency: 100% human creation is valued at the standard rate, while AI-generated tracks are capped at 25% to prevent streaming fraud. Why 25%? This threshold makes AI-generated bulk streaming economically unviable as fraud — below the payout break-even for bot farms — while still fairly compensating legitimate AI-assisted works.',
    navigateTo: '/creators/fair-pay',
    targetSelector: '[data-tour="fair-pay-chart"]',
    tooltipSide: 'bottom',
    cta: 'Next',
  },
  {
    id: 'copyright-chain',
    type: 'spotlight',
    headline: 'Inviolable Digital Fingerprints.',
    title: 'Your Musical DNA is Tamper-Proof',
    body: 'Once uploaded elsewhere, you lose control of your work. Here, you can drag and drop a test file to instantly generate a 512-bit MusicDNA hash on the blockchain. This is how we protect your art from unauthorized AI clones and wild remixes.',
    navigateTo: '/creators/register-song',
    targetSelector: '[data-tour="register-song"]',
    tooltipSide: 'bottom',
    cta: 'Next',
  },
  {
    id: 'governance',
    type: 'spotlight',
    headline: 'Power Back to the Industry.',
    title: 'You Set the Rules',
    body: 'Wall Street does not dictate our rules -- you do. Experience our DAO voting simulator, safeguarded by the IMC\'s Golden Share veto right. During this beta, use the contextual feedback buttons to share your expert opinions directly with our engineering and legal teams. Every vote shapes the platform.',
    targetSelector: '[data-tour="feedback-button"]',
    tooltipSide: 'top',
    cta: 'Next',
  },
  {
    id: 'recommendations',
    type: 'spotlight',
    headline: 'Listeners are in Control.',
    title: 'Transparent Recommendation Algorithm',
    body: 'Recommendation algorithms on classical platforms are opaque -- there is no real chance for an obscure band to appear on your feed. Our algorithm is entirely transparent, customizable, and unbiased. You are in control.',
    navigateTo: '/creators/recommendations',
    targetSelector: '[data-tour="recommendations"]',
    tooltipSide: 'bottom',
    cta: 'Next',
  },
  {
    id: 'and-more',
    type: 'modal',
    title: 'And Much More',
    cta: 'Finish Tour & Explore',
  },
];

const TOOLTIP_WIDTH = 360;
const SPOTLIGHT_PADDING = 16;

export function OnboardingTour() {
  const {user, isLoggedIn, hasPermission} = useAuth();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [navigating, setNavigating] = useState(false);
  const completeTour = useCompleteTour();
  const navigate = useNavigate();
  const location = useLocation();
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-show for everyone except admin users
  useEffect(() => {
    if (hasPermission('admin')) return;
    // Also skip if user is on any admin page
    if (window.location.pathname.startsWith('/admin')) return;
    const t = setTimeout(() => tourStore.show(), 800);
    return () => clearTimeout(t);
  }, [hasPermission]);

  // Hide tour immediately if user navigates to admin or is admin
  useEffect(() => {
    if (hasPermission('admin') || location.pathname.startsWith('/admin')) {
      setVisible(false);
    }
  }, [hasPermission, location.pathname]);

  useEffect(() => {
    return tourStore.subscribe(show => {
      setVisible(show);
      if (show) setStep(0);
    });
  }, []);

  // Resolve target rect for spotlight steps
  useEffect(() => {
    if (!visible) return;
    const current = STEPS[step];
    if (current.type !== 'spotlight' || !current.targetSelector) {
      setTargetRect(null);
      return;
    }

    if (pollRef.current) clearTimeout(pollRef.current);
    setTargetRect(null);

    let attempts = 0;
    const poll = () => {
      const el = document.querySelector(current.targetSelector!);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        setNavigating(false);
      } else if (attempts < 40) {
        attempts++;
        pollRef.current = setTimeout(poll, 150);
      } else {
        // Element not found after ~6s — show tooltip centred without spotlight
        setNavigating(false);
      }
    };
    pollRef.current = setTimeout(poll, 300);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [visible, step]);

  // Update rect on resize
  useEffect(() => {
    if (!visible || !targetRect) return;
    const current = STEPS[step];
    if (!current.targetSelector) return;

    const onResize = () => {
      const el = document.querySelector(current.targetSelector!);
      if (el) setTargetRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visible, step, targetRect]);

  if (!visible) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  function close() {
    setVisible(false);
    setNavigating(false);
    completeTour.mutate();
  }

  function advance() {
    if (isLast) {
      close();
      return;
    }
    const next = STEPS[step + 1];
    setTargetRect(null);
    if (next.navigateTo) {
      setNavigating(true);
      navigate(next.navigateTo);
    } else {
      setNavigating(false);
    }
    setStep(s => s + 1);
  }

  function jumpTo(i: number) {
    const target = STEPS[i];
    setTargetRect(null);
    if (target.navigateTo) {
      setNavigating(true);
      navigate(target.navigateTo);
    } else {
      setNavigating(false);
    }
    setStep(i);
  }

  function handleCta() {
    if (current.ctaPath) {
      navigate(current.ctaPath);
    }
    advance();
  }

  const StepDots = () => (
    <div className="flex gap-6">
      {STEPS.map((_, i) => (
        <button
          key={i}
          onClick={() => jumpTo(i)}
          className={`h-6 rounded-full transition-all ${
            i === step
              ? 'w-20 bg-primary'
              : i < step
                ? 'w-6 bg-primary/40'
                : 'w-6 bg-white/15'
          }`}
          aria-label={`Go to step ${i + 1}`}
        />
      ))}
    </div>
  );

  if (current.type === 'modal') {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-16 backdrop-blur-sm">
        {current.id === 'welcome' ? (
          <WelcomeModal
            dots={<StepDots />}
            onStart={advance}
            onSkip={close}
          />
        ) : (
          <AndMoreModal dots={<StepDots />} onFinish={close} />
        )}
      </div>
    );
  }

  // Spotlight step — disabled on mobile viewports (centered modal fallback)
  const showSpotlight = targetRect !== null && !navigating && window.innerWidth >= 768;

  return (
    <>
      {showSpotlight ? (
        <SpotlightOverlay rect={targetRect!} />
      ) : (
        <div className="fixed inset-0 z-[9998] bg-black/70" />
      )}

      {/* Tooltip card */}
      <div
        className="fixed z-[10000] w-[calc(100vw-32px)] max-w-[360px] rounded-2xl border border-white/10 bg-paper shadow-2xl"
        style={showSpotlight ? tooltipStyle(targetRect!, current.tooltipSide ?? 'bottom') : centeredStyle()}
      >
        <div className="border-b border-white/6 px-24 py-16">
          <StepDots />
        </div>

        <div className="max-h-[calc(80vh-60px)] overflow-y-auto px-24 py-24">
          {navigating ? (
            <div className="flex h-32 items-center justify-center text-sm text-muted">
              Loading...
            </div>
          ) : (
            <>
              {current.headline && (
                <div className="mb-6 text-xs font-bold uppercase tracking-wide text-primary">
                  {current.headline}
                </div>
              )}
              <h2 className="mb-10 text-lg font-bold">{current.title}</h2>
              {current.body && (
                <p className="mb-24 text-sm leading-relaxed text-muted">
                  {current.body}
                </p>
              )}
              <button
                onClick={advance}
                className="rounded-lg bg-primary px-20 py-8 text-sm font-semibold text-on-primary transition-opacity hover:opacity-90"
              >
                {isLast ? 'Finish' : (current.cta ?? 'Next')}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const TOOLTIP_ESTIMATED_HEIGHT = 400;
const SCREEN_MARGIN = 16;

function tooltipStyle(rect: DOMRect, side: TooltipSide): React.CSSProperties {
  if (window.innerWidth < 768) return centeredStyle();

  const pad = SPOTLIGHT_PADDING;
  const gap = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2, SCREEN_MARGIN),
    vw - TOOLTIP_WIDTH - SCREEN_MARGIN,
  );

  // Try the preferred side; fall back to the opposite if it would clip
  const wantsTop = side === 'top';
  const spaceAbove = rect.top - pad - gap;
  const spaceBelow = vh - (rect.bottom + pad + gap);
  const fitsAbove = spaceAbove >= TOOLTIP_ESTIMATED_HEIGHT;
  const fitsBelow = spaceBelow >= TOOLTIP_ESTIMATED_HEIGHT;

  const useTop = wantsTop ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;

  if (useTop) {
    // Position card above the spotlight; anchor via bottom so card grows upward
    const bottom = vh - (rect.top - pad - gap);
    return {bottom: Math.max(bottom, SCREEN_MARGIN), left};
  }

  // Position card below the spotlight
  const top = rect.bottom + pad + gap;
  return {top: Math.min(top, vh - TOOLTIP_ESTIMATED_HEIGHT - SCREEN_MARGIN), left};
}

function centeredStyle(): React.CSSProperties {
  return {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
}

function SpotlightOverlay({rect}: {rect: DOMRect}) {
  const p = SPOTLIGHT_PADDING;
  const x = rect.left - p;
  const y = rect.top - p;
  const w = rect.width + p * 2;
  const h = rect.height + p * 2;

  return (
    <svg
      className="pointer-events-none fixed inset-0 z-[9998]"
      width="100%"
      height="100%"
      style={{width: '100vw', height: '100vh'}}
    >
      <defs>
        <mask id="tour-spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect x={x} y={y} width={w} height={h} rx="8" fill="black" />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(0,0,0,0.78)"
        mask="url(#tour-spotlight-mask)"
      />
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx="8"
        fill="none"
        stroke="rgba(59,130,246,0.7)"
        strokeWidth="2"
      />
    </svg>
  );
}

function WelcomeModal({
  dots,
  onStart,
  onSkip,
}: {
  dots: React.ReactNode;
  onStart: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="relative w-full max-w-560 rounded-2xl border border-white/10 bg-paper shadow-2xl">
      <div className="border-b border-white/6 px-32 py-20">
        {dots}
      </div>

      <div className="max-h-[80vh] overflow-y-auto px-32 py-32">
        <div className="mb-6 text-xs font-bold uppercase tracking-wide text-amber-400">
          Beta Test
        </div>

        <h1 className="mb-16 text-2xl font-bold leading-snug">
          Welcome to Fair Music Beta
        </h1>

        <p className="mb-12 text-sm leading-relaxed text-muted">
          Fair Music is a new streaming platform built around fairness, transparency, and artist empowerment. We're creating a better way for artists to earn, connect with fans, and retain control over their work — while giving listeners a more personal and rewarding music experience.
        </p>
        <p className="mb-12 text-sm leading-relaxed text-muted">
          As a beta tester, you'll help shape the future of the platform through your feedback, feature suggestions, and bug reports. In return, you'll earn rewards and gain early access to a community-driven music ecosystem.
        </p>
        <p className="mb-4 text-sm leading-relaxed text-muted">
          Whether you're an artist or a music fan, this is your chance to be part of a fairer future for music.
        </p>
        <p className="mb-20 text-sm font-semibold text-primary">
          Join the beta. Test the future. Help build Fair Music.
        </p>

        <button
          onClick={onStart}
          className="w-full rounded-lg bg-primary px-24 py-12 text-base font-bold text-on-primary transition-opacity hover:opacity-90"
        >
          Take the Tour
        </button>
        <button
          onClick={onSkip}
          className="mt-10 w-full rounded-lg px-24 py-10 text-sm font-medium text-muted transition-colors hover:text-main"
        >
          Skip the Tour
        </button>
      </div>
    </div>
  );
}

const MORE_FEATURES = [
  {
    label: 'Indie Discovery',
    desc: 'Algorithm-powered discovery that gives independent artists equal footing.',
    path: '/indie',
    icon: '~',
  },
  {
    label: 'Register Now',
    desc: 'Upload tracks, manage royalty splits, apply for copyright registration.',
    path: '/backstage/requests',
    icon: '*',
  },
  {
    label: 'Fair Pay Dashboard',
    desc: 'See exactly how revenue is calculated and distributed.',
    path: '/creators/fair-pay',
    icon: '%',
  },
  {
    label: 'Recommendation Engine',
    desc: 'Transparent, customizable, bias-free discovery settings.',
    path: '/creators/recommendations',
    icon: '&',
  },
  {
    label: 'Copyright Registration',
    desc: 'Generate an immutable MusicDNA hash for your work.',
    path: '/creators/register-song',
    icon: '#',
  },
  {
    label: 'Send Feedback',
    desc: 'Your expert input shapes the platform. Every vote counts.',
    path: null,
    icon: '!',
  },
];

function AndMoreModal({
  dots,
  onFinish,
}: {
  dots: React.ReactNode;
  onFinish: () => void;
}) {
  return (
    <div className="relative w-full max-w-600 rounded-2xl border border-white/10 bg-paper shadow-2xl">
      <div className="border-b border-white/6 px-32 py-20">
        {dots}
      </div>

      <div className="px-32 py-32">
        <div className="mb-6 text-xs font-bold uppercase tracking-wide text-primary">
          Explore the Platform
        </div>
        <h2 className="mb-6 text-xl font-bold">And Much More</h2>
        <p className="mb-24 text-sm leading-relaxed text-muted">
          Here is a quick map of everything available during the beta.
        </p>

        <div className="mb-28 grid grid-cols-2 gap-10">
          {MORE_FEATURES.map(f => (
            <FeatureCard key={f.label} feature={f} onFinish={onFinish} />
          ))}
        </div>

        <button
          onClick={onFinish}
          className="w-full rounded-lg bg-primary px-24 py-10 text-sm font-semibold text-on-primary transition-opacity hover:opacity-90"
        >
          Finish Tour &amp; Explore
        </button>
      </div>
    </div>
  );
}

function FeatureCard({
  feature,
  onFinish,
}: {
  feature: (typeof MORE_FEATURES)[0];
  onFinish: () => void;
}) {
  const inner = (
    <div className="flex h-full flex-col gap-6 rounded-xl border border-white/8 bg-elevated p-14 text-left transition-colors hover:border-primary/40 hover:bg-primary/5">
      <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-primary/10 text-lg font-bold text-primary">
        {feature.icon}
      </div>
      <div className="text-sm font-semibold">{feature.label}</div>
      <div className="text-xs leading-relaxed text-muted">{feature.desc}</div>
    </div>
  );

  if (!feature.path) {
    return <div>{inner}</div>;
  }

  return (
    <Link to={feature.path} onClick={onFinish}>
      {inner}
    </Link>
  );
}
